/**
 * Typed client for the per-SITE auto-refund setting (ADR-0099 Slice 2b).
 *
 *   GET  /v1/sites/:site_id            — the site response carries an
 *        `auto_refund: { enabled, max_minor }` sub-object ONLY for
 *        owner/admin (the field is omitted for reviewer/viewer — the
 *        money-checkpoint config is not exposed to roles that can't set
 *        it). Absent field ⇒ treated as `forbidden`.
 *   PUT  /v1/sites/:site_id/auto-refund — owner/admin sets it. Body
 *        `{ enabled: boolean, max_minor?: number }`. Enabling requires a
 *        positive integer cap (server 422 `auto_refund_cap_required`);
 *        a non-integer/≤0/over-ceiling cap is 422 `invalid_max_minor`.
 *
 * `max_minor` is the cap in MINOR units (cents). The UI converts to/from
 * major units at the edge.
 */

import { ApiError, type HttpClient } from './http-client.js';

export type GetSiteAutoRefundResult =
  | { readonly kind: 'ok'; readonly enabled: boolean; readonly max_minor: number | null }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export type PutSiteAutoRefundResult =
  | { readonly kind: 'ok'; readonly enabled: boolean; readonly max_minor: number | null }
  | { readonly kind: 'cap_required' }
  | { readonly kind: 'invalid_max' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

interface SiteGetWire {
  readonly auto_refund?: { readonly enabled?: unknown; readonly max_minor?: unknown };
}

interface PutWire {
  readonly auto_refund_enabled?: unknown;
  readonly auto_refund_max_minor?: unknown;
}

function coerceMaxMinor(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

export async function getSiteAutoRefund(
  http: HttpClient,
  site_id: string,
): Promise<GetSiteAutoRefundResult> {
  try {
    const res = await http.request<SiteGetWire>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    // The field is present only for owner/admin; its absence means the
    // caller's role cannot see (or set) the auto-refund config.
    if (res.auto_refund === undefined) return { kind: 'forbidden' };
    return {
      kind: 'ok',
      enabled: res.auto_refund.enabled === true,
      max_minor: coerceMaxMinor(res.auto_refund.max_minor),
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

export async function putSiteAutoRefund(
  http: HttpClient,
  site_id: string,
  input: { readonly enabled: boolean; readonly max_minor?: number },
): Promise<PutSiteAutoRefundResult> {
  try {
    const res = await http.request<PutWire>({
      method: 'PUT',
      path: `/v1/sites/${encodeURIComponent(site_id)}/auto-refund`,
      body: input.max_minor === undefined
        ? { enabled: input.enabled }
        : { enabled: input.enabled, max_minor: input.max_minor },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return {
      kind: 'ok',
      enabled: res.auto_refund_enabled === true,
      max_minor: coerceMaxMinor(res.auto_refund_max_minor),
    };
  } catch (err) {
    if (err instanceof ApiError) {
      // Both validation failures arrive as 422 (server_error kind) with a
      // discriminating `reason`.
      if (err.server_reason === 'auto_refund_cap_required') return { kind: 'cap_required' };
      if (err.server_reason === 'invalid_max_minor') return { kind: 'invalid_max' };
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'bad_request') return { kind: 'invalid_max' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Auto-refund service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
